import { useIndirectTaxFilingV2 } from '@commenda/api/indirect-tax/filings';
import { Button, useToggle } from '@commenda/kakashi';
import { TaskParty, TaskStatus } from '@prisma/client';
import { useState } from 'react';

import { IndirectTaxRegionHelper } from '../../../indirect-tax/common';
import { ServiceRequestTaskLabel } from '../../common/labels';
import { ServiceRequestTaskIconConfig } from '../../common/taskIconConfig';
import { ServiceRequestTaskCard } from '../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../types';
import { IndirectTaxFilingConfirmationModal } from './components/IndirectTaxFilingConfirmationModal';
import { IndirectTaxFilingVatConfirmModal } from './components/IndirectTaxFilingVatConfirmModal';
import { ConfirmFilingTask } from './ConfirmFilingTask';

export const IndirectTaxFilingSubmitTask = (props: TCommonTaskProp) => {
  const { task, context, isTimelineView, displayNumber } = props;
  const { role, serviceRequest, mutate: refetchServiceRequest } = context;

  const filingId =
    serviceRequest?.salesTaxFiling?.filing_id ??
    serviceRequest?.salesTaxFilingId;

  const { filing, mutate: refetchFiling } = useIndirectTaxFilingV2(
    filingId,
    filingId
      ? {
          corporationId: serviceRequest.corporationId,
          includeFiledByAgent: true,
        }
      : null
  );

  const confirmModal = useToggle();
  const [viewMode, setViewMode] = useState(false);

  if (!filingId) {
    return <ConfirmFilingTask {...props} />;
  }

  const isContributionDriven = filing
    ? IndirectTaxRegionHelper.isContributionRegion(
        IndirectTaxRegionHelper.deriveRegionFromJurisdiction({
          jurisdictionType: filing.jurisdictionType,
          jurisdictionName: filing.jurisdictionName,
          country: filing.country,
        })
      )
    : false;

  const onFilingCompleted = () => {
    refetchFiling();
    if (refetchServiceRequest) refetchServiceRequest();
  };

  const openConfirmModal = (viewOnly: boolean) => {
    setViewMode(viewOnly);
    confirmModal.show();
  };

  const triggerButton = (
    <div className="flex flex-col items-start gap-sm">
      {filing && !filing.filingLockedAt && (
        <p className="txt-extra-small-regular text-text-warning">
          Client hasn’t approved this filing yet.
        </p>
      )}
      <Button
        variant="secondary"
        size="sm"
        onClick={() => openConfirmModal(false)}
      >
        {isContributionDriven ? 'Confirm filing' : 'Submit filing'}
      </Button>
    </div>
  );

  const editButton = isContributionDriven ? null : (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => openConfirmModal(false)}
    >
      Edit
    </Button>
  );

  const filedByDescription = filing?.filedByAgent?.name ? (
    <p className="txt-extra-small-regular text-text-secondary">
      Filed by {filing.filedByAgent.name}
    </p>
  ) : undefined;

  const agentActiveLabel = isContributionDriven
    ? 'Confirm the filing and upload the acknowledgement'
    : 'Submit filing to the tax authority';

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.CLIENT]: {
          [TaskStatus.TO_DO]: {
            actions: null,
            label: <ServiceRequestTaskLabel label="Filing submission" />,
          },
          [TaskStatus.IN_PROGRESS]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label="Commenda team is submitting your filing"
                status={task.status}
              />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label="Filing submitted"
                status={task.status}
              />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: null,
            label: <ServiceRequestTaskLabel label="Filing submission" />,
          },
        },

        [TaskParty.AGENT]: {
          [TaskStatus.TO_DO]: {
            actions: triggerButton,
            label: <ServiceRequestTaskLabel label={agentActiveLabel} />,
          },
          [TaskStatus.IN_PROGRESS]: {
            actions: triggerButton,
            label: (
              <ServiceRequestTaskLabel
                label={agentActiveLabel}
                status={task.status}
              />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: editButton,
            label: (
              <ServiceRequestTaskLabel
                label="Filing submitted"
                status={task.status}
              />
            ),
            description: filedByDescription,
          },
          [TaskStatus.BACKLOG]: {
            actions: null,
            label: <ServiceRequestTaskLabel label={agentActiveLabel} />,
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <>
      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />

      {isContributionDriven ? (
        <IndirectTaxFilingVatConfirmModal
          isOpen={confirmModal.visible}
          onClose={confirmModal.off}
          filingId={filingId}
          corporationId={serviceRequest.corporationId}
          companyId={serviceRequest.companyId}
          onComplete={onFilingCompleted}
        />
      ) : (
        <IndirectTaxFilingConfirmationModal
          isOpen={confirmModal.visible}
          onClose={confirmModal.off}
          filingId={filingId}
          corporationId={serviceRequest.corporationId}
          companyId={serviceRequest.companyId}
          onComplete={onFilingCompleted}
          viewMode={viewMode}
        />
      )}
    </>
  );
};
